export const SWITCHOVER_TEMPERATURES = [-25, -20, -15, -10, -5, 0, 5];

/** Initial 5°C sweep anchors shown on the progress line (refinement °C stay off the line). */
export const isCoreSwitchoverTemperature = (temperature) => Number(temperature) % 5 === 0;

export const coreSwitchoverTemperatures = (temperatures = []) =>
    [...temperatures]
        .filter(isCoreSwitchoverTemperature)
        .sort((a, b) => Number(a) - Number(b));

export const refinedSwitchoverTemperatures = (temperatures = []) =>
    [...temperatures]
        .filter((temperature) => !isCoreSwitchoverTemperature(temperature))
        .sort((a, b) => Number(a) - Number(b));

/** Anchor vs 1°C refinement counts for status copy. */
export const hybridTemperatureProgressSummary = (temperatures = []) => {
    const anchor = coreSwitchoverTemperatures(temperatures);
    const refined = refinedSwitchoverTemperatures(temperatures);
    return {
        anchorTotal: anchor.length,
        refinedTotal: refined.length,
        total: temperatures.length,
    };
};

/** 5°C anchor pairs whose interior 1°C points are not yet on the run. */
export const refinableHybridRanges = (switchoverTemperatures = [], completedTemperatures = []) => {
    const existing = new Set((switchoverTemperatures || []).map((value) => Number(value)));
    const completed = new Set((completedTemperatures || []).map((value) => Number(value)));
    const anchors = [...existing].sort((a, b) => b - a);
    const ranges = [];

    for (let i = 0; i < anchors.length; i += 1) {
        for (let j = i + 1; j < anchors.length; j += 1) {
            const warmerC = anchors[i];
            const colderC = anchors[j];
            if (warmerC - colderC !== 5) continue;
            if (!completed.has(warmerC) || !completed.has(colderC)) continue;

            const missing = [];
            for (let temp = colderC + 1; temp < warmerC; temp += 1) {
                if (!existing.has(temp)) missing.push(temp);
            }
            if (missing.length) {
                ranges.push({ fromC: warmerC, toC: colderC, missing });
            }
        }
    }

    return ranges;
};

/** Each 5°C pair on the run, with refinable / refined state for progress-line segments. */
export const hybridFiveDegreeSegments = (switchoverTemperatures = [], completedTemperatures = []) => {
    const existing = new Set((switchoverTemperatures || []).map((value) => Number(value)));
    const completed = new Set((completedTemperatures || []).map((value) => Number(value)));
    const segments = [];

    existing.forEach((warmerC) => {
        const colderC = warmerC - 5;
        if (!existing.has(colderC)) return;

        const missing = [];
        for (let temp = colderC + 1; temp < warmerC; temp += 1) {
            if (!existing.has(temp)) missing.push(temp);
        }

        const endpointsDone = completed.has(warmerC) && completed.has(colderC);
        let interiorDone = true;
        for (let temp = colderC + 1; temp < warmerC; temp += 1) {
            if (!completed.has(temp)) interiorDone = false;
        }

        if (!isCoreSwitchoverTemperature(warmerC) || !isCoreSwitchoverTemperature(colderC)) return;

        segments.push({
            fromC: warmerC,
            toC: colderC,
            missing,
            refinable: endpointsDone && missing.length > 0,
            refined: endpointsDone && missing.length === 0 && interiorDone,
            refinementRunning: endpointsDone && missing.length === 0 && !interiorDone,
        });
    });

    return segments.sort((a, b) => b.fromC - a.fromC);
};

export const ALLOWED_H2K_VERSIONS = ["v11-11", "v11-12", "v11-13"];

// Ids match furnaceEnergySource / boilerEnergySource in clientFields.
const FURNACE_BOILER_FUELS = {
    0: { key: "electricity", label: "Electric", eligible: false, reason: "electric" },
    1: { key: "naturalGas", label: "Natural gas", eligible: true },
    2: { key: "oil", label: "Oil", eligible: true },
    3: { key: "propane", label: "Propane", eligible: true },
    4: { key: "wood", label: "Mixed wood", eligible: false, reason: "wood" },
    5: { key: "wood", label: "Hardwood", eligible: false, reason: "wood" },
    6: { key: "wood", label: "Softwood", eligible: false, reason: "wood" },
    7: { key: "wood", label: "Wood pellets", eligible: false, reason: "wood" },
};

// Ids match comboEnergySource and p9EnergySource. Those lists start at natural gas.
const COMBO_FUELS = {
    0: { key: "naturalGas", label: "Natural gas", eligible: true },
    1: { key: "oil", label: "Oil", eligible: true },
    2: { key: "propane", label: "Propane", eligible: true },
};

const HEATING_TYPE_LABELS = {
    furnace: "furnace",
    boiler: "boiler",
    combo: "combo system",
    p9Combo: "P.9 combo",
    baseboards: "baseboards",
};

const overlaySection = (current = {}, upgradeSection) => {
    const nextSystem = upgradeSection?.system;
    if (!nextSystem || Object.keys(nextSystem).length === 0) return current;
    return {
        ...current,
        ...upgradeSection,
        system: nextSystem,
    };
};

/** Heating and cooling after the selected upgrade package is applied. */
export const applyPackageHeatingCooling = (heatingCooling = {}, upgradePackage) => {
    let heating = heatingCooling.heating || {};
    let cooling = heatingCooling.cooling || {};

    Object.values(upgradePackage?.upgrades || {}).forEach((upgrade) => {
        if (upgrade?.upgradeType !== "heatingCooling") return;
        heating = overlaySection(heating, upgrade.fields?.heating);
        cooling = overlaySection(cooling, upgrade.fields?.cooling);
    });

    return { heating, cooling };
};

const firstSystem = (section = {}) => {
    const system = section.system || {};
    const key = Object.keys(system)[0] || "";
    return { key, system: key ? system[key] || {} : {} };
};

const backupFuelFor = (key, system) => {
    if (key === "baseboards") {
        return { key: "electricity", label: "Electric", eligible: false, reason: "electric" };
    }
    if (key === "combo") return COMBO_FUELS[system.energySource?.id] || null;
    if (key === "p9Combo") return COMBO_FUELS[system.testData?.energySource?.id] || null;
    if (key === "furnace" || key === "boiler") return FURNACE_BOILER_FUELS[system.energySource?.id] || null;
    return null;
};

const backupDetail = (key, fuel) => {
    if (!key) return "This case has no heating system. Add a natural gas, propane, or oil system.";
    if (key === "baseboards") return "Electric baseboards cannot be the backup system for this analysis.";
    if (!fuel) return "This heating system is not a natural gas, propane, or oil furnace, boiler, or combo.";
    if (fuel.reason === "electric") return "The heating system is electric. Use natural gas, propane, or oil.";
    if (fuel.reason === "wood") {
        return "Wood backup is not included in this analysis, because reliable wood prices are not available.";
    }
    return "";
};

const heatPumpDetail = (key) => {
    if (!key) return "This case has no cooling system. Add an air-source heat pump.";
    if (key === "airConditioning") return "This case has air conditioning. Add an air-source heat pump.";
    if (key === "groundHeatPump") return "Ground-source heat pumps are not included in this analysis.";
    if (key === "waterHeatPump") return "Water-source heat pumps are not included in this analysis.";
    if (key !== "airHeatPump") return "This case needs an air-source heat pump.";
    return "";
};

/**
 * Whether the selected case, after upgrades, is a hybrid heating system
 * this analysis can run: an air-source heat pump plus gas, propane, or oil backup.
 */
export const evaluateHybridCase = (heatingCooling = {}, upgradePackage) => {
    const { heating, cooling } = applyPackageHeatingCooling(heatingCooling, upgradePackage);
    const heatPump = firstSystem(cooling);
    const backup = firstSystem(heating);
    const fuel = backupFuelFor(backup.key, backup.system);
    const heatPumpOk = heatPump.key === "airHeatPump";
    const backupOk = Boolean(fuel?.eligible);
    const coldClimate = Boolean(heatPump.system?.ccashp);

    const heatPumpLabel = heatPumpOk
        ? coldClimate
            ? "Cold-climate air-source heat pump"
            : "Air-source heat pump"
        : heatPump.key === "groundHeatPump"
          ? "Ground-source heat pump"
          : heatPump.key === "waterHeatPump"
            ? "Water-source heat pump"
            : heatPump.key === "airConditioning"
              ? "Air conditioner"
              : "No air-source heat pump";

    const typeLabel = HEATING_TYPE_LABELS[backup.key] || "heating system";
    const backupLabel = backupOk ? `${fuel.label} ${typeLabel}` : fuel?.label || "No fossil-fuel backup";
    const heatingConfiguration = !backup.key
        ? "No heating system"
        : fuel?.label
          ? `${fuel.label} ${typeLabel}`
          : backup.key === "baseboards"
            ? "Electric baseboards"
            : typeLabel;
    const cutoffType = heatPump.system?.temperature?.cutoffType || {};
    const savedCutoffLabel = !heatPumpOk
        ? ""
        : cutoffType.id === 1 && cutoffType.value != null
          ? `Restricted at ${cutoffType.value}°C`
          : cutoffType.id === 2
            ? "Unrestricted"
            : cutoffType.id === 0
              ? "Balance point"
              : "Not set";

    return {
        heatPumpOk,
        backupOk,
        ready: heatPumpOk && backupOk,
        fuelKey: backupOk ? fuel.key : "",
        heatPumpLabel,
        backupLabel,
        heatingConfiguration,
        savedCutoffLabel,
        heatPumpDetail: heatPumpDetail(heatPump.key),
        backupDetail: backupOk ? "" : backupDetail(backup.key, fuel),
    };
};
